import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Plus, Trash2, Edit3, X, Cake, Calendar, Instagram, Link2, ShoppingBag, Heart, MapPin, Sparkles, Loader2, Eye, Upload, ExternalLink, Radar, Mail } from "lucide-react";
import { CelestialSymbol } from "@/components/CelestialCard";

const COLORS = ["#7c3aed", "#10b981", "#a855f7", "#059669", "#d4af37", "#ec4899", "#6366f1"];
const emptyForm = {
  name: "", relationship: "", personality: "", interests: "",
  special_dates: [], date_of_birth: "", avatar_color: "#7c3aed",
  social_connected: false, pinterest_linked: false, etsy_linked: false,
  pinterest_url: "", instagram_url: "", linkedin_url: "",
  email: "", phone: "",
  address: { name: "", address_line1: "", address_line2: "", address_city: "", address_state: "", address_zip: "", address_country: "US" },
};

const LovedOnes = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [sdLabel, setSdLabel] = useState("Birthday");
  const [sdDate, setSdDate] = useState("");
  const [saving, setSaving] = useState(false);

  // AI Shopper state
  const [shopperOpen, setShopperOpen] = useState(false);
  const [shopperLo, setShopperLo] = useState(null);
  const [shopperOccasion, setShopperOccasion] = useState("upcoming milestone");
  const [shopperLoading, setShopperLoading] = useState(false);
  const [shopperError, setShopperError] = useState("");
  const [boards, setBoards] = useState([]);

  // Vision Agent state
  const [styleProfile, setStyleProfile] = useState(null);
  const [visionLoading, setVisionLoading] = useState(false);
  const [visionError, setVisionError] = useState("");
  const [uploadedImages, setUploadedImages] = useState([]);

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    const b64s = await Promise.all(files.map(fileToBase64));
    setUploadedImages(b64s);
  };

  const runVision = async () => {
    setVisionLoading(true);
    setVisionError("");
    try {
      const res = await api.post(`/loved-ones/${shopperLo.id}/vision-analyze`, { images_base64: uploadedImages });
      setStyleProfile(res.data.style_profile);
    } catch (e) {
      setVisionError(e?.response?.data?.detail || "Vision analysis failed. Upload reference images or add a Pinterest URL.");
    } finally {
      setVisionLoading(false);
    }
  };

  const openShopper = async (lo) => {
    setShopperLo(lo);
    setShopperOpen(true);
    setShopperError("");
    setBoards([]);
    setStyleProfile(lo.style_profile || null);
    setUploadedImages([]);
    setVisionError("");
    try {
      const res = await api.get(`/loved-ones/${lo.id}/mood-boards`);
      if (res.data.length) setBoards(res.data[0].boards);
    } catch (e) { console.error("Mood boards fetch failed:", e); }
  };

  const runShopper = async () => {
    setShopperLoading(true);
    setShopperError("");
    try {
      const res = await api.post(`/loved-ones/${shopperLo.id}/mood-boards`, { occasion: shopperOccasion });
      setBoards(res.data.boards);
    } catch (e) {
      setShopperError(e?.response?.data?.detail || "Failed to generate mood boards.");
    } finally {
      setShopperLoading(false);
    }
  };

  const load = useCallback(async () => {
    const res = await api.get("/loved-ones");
    setItems(res.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(emptyForm); setEditId(null); setOpen(true); };
  const openEdit = (lo) => {
    setForm({ ...emptyForm, ...lo, address: { ...emptyForm.address, ...(lo.address || {}) } });
    setEditId(lo.id);
    setOpen(true);
  };

  const addSpecialDate = () => {
    if (!sdDate) return;
    setForm((f) => ({ ...f, special_dates: [...f.special_dates, { label: sdLabel, date: sdDate, recurring: true }] }));
    setSdDate("");
  };
  const removeSpecialDate = (i) =>
    setForm((f) => ({ ...f, special_dates: f.special_dates.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (editId) await api.put(`/loved-ones/${editId}`, payload);
      else await api.post("/loved-ones", payload);
      setOpen(false);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this loved one and their milestones?")) return;
    await api.delete(`/loved-ones/${id}`);
    await load();
  };

  const toggle = (key) => setForm((f) => ({ ...f, [key]: !f[key] }));

  const [autoDrafting, setAutoDrafting] = useState(null);
  const runAutonomousDraft = async (lo) => {
    setAutoDrafting(lo.id);
    try {
      await api.post(`/loved-ones/${lo.id}/autonomous-draft`, { occasion: "Birthday", tone: "warm and heartfelt" });
      alert(`Autonomous draft created for ${lo.name}! Review it under Curations.`);
      navigate("/drafts");
    } catch (e) {
      alert(e?.response?.data?.detail || "Autonomous draft failed.");
    } finally {
      setAutoDrafting(null);
    }
  };

  const [pulsing, setPulsing] = useState(null);
  const runPulse = async (lo) => {
    setPulsing(lo.id);
    try {
      const res = await api.post(`/loved-ones/${lo.id}/social-pulse`);
      await load();
      const major = res.data.major_event;
      if (major) {
        alert(`Social Pulse detected a milestone for ${lo.name}: ${major.detail}. A matching design was auto-curated — see Curations.`);
      } else {
        const n = (res.data.milestones || []).length;
        alert(`Social Pulse complete for ${lo.name}. ${n} signal(s) inferred (simulated). No major life event this cycle.`);
      }
    } catch (e) {
      alert(e?.response?.data?.detail || "Social Pulse failed.");
    } finally {
      setPulsing(null);
    }
  };

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-4xl mb-1">Your Loved Ones</h1>
          <p className="text-muted-foreground">The people worth remembering, always.</p>
        </div>
        <button
          data-testid="add-loved-one-btn"
          onClick={openNew}
          className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-secondary text-white px-4 py-2.5 rounded-lg hover:opacity-90"
        >
          <Plus className="w-4 h-4" /> Add Loved One
        </button>
      </div>

      {items.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center">
          <Heart className="w-12 h-12 mx-auto mb-4 text-secondary/60" />
          <h3 className="font-serif text-2xl mb-2">Start your circle</h3>
          <p className="text-muted-foreground">Add someone special to begin tracking their milestones.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="loved-ones-grid">
          {items.map((lo) => (
            <div key={lo.id} className="glass rounded-2xl p-6 card-hover">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-serif text-xl"
                    style={{ background: lo.avatar_color }}>
                    {lo.name?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <div className="font-serif text-lg leading-tight">{lo.name}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      {lo.relationship || "Loved one"}
                      <CelestialSymbol zodiac={lo.zodiac} />
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(lo)} className="p-2 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground" data-testid="edit-loved-one-btn">
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(lo.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-400" data-testid="delete-loved-one-btn">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {lo.personality && <p className="text-sm text-muted-foreground mb-3 line-clamp-2 italic">&ldquo;{lo.personality}&rdquo;</p>}

              <div className="space-y-1.5 mb-4">
                {lo.special_dates?.length ? lo.special_dates.map((sd, i) => (
                  <div key={`${sd.label}-${sd.date}-${i}`} className="flex items-center gap-2 text-sm">
                    <Cake className="w-3.5 h-3.5 gold-text" />
                    <span className="text-foreground/80">{sd.label}</span>
                    <span className="text-muted-foreground">
                      · {new Date(sd.date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                  </div>
                )) : <div className="text-xs text-muted-foreground">No special dates yet</div>}
              </div>

              <div className="flex gap-2 pt-3 border-t border-border/50">
                <span className={`text-xs px-2 py-1 rounded-full border flex items-center gap-1 ${lo.social_connected ? "bg-secondary/15 text-secondary border-secondary/30" : "bg-muted/40 text-muted-foreground border-border"}`}>
                  <Instagram className="w-3 h-3" /> Social
                </span>
                <span className={`text-xs px-2 py-1 rounded-full border flex items-center gap-1 ${lo.pinterest_linked ? "bg-secondary/15 text-secondary border-secondary/30" : "bg-muted/40 text-muted-foreground border-border"}`}>
                  <Link2 className="w-3 h-3" /> Pinterest
                </span>
                <span className={`text-xs px-2 py-1 rounded-full border flex items-center gap-1 ${lo.etsy_linked ? "bg-secondary/15 text-secondary border-secondary/30" : "bg-muted/40 text-muted-foreground border-border"}`}>
                  <ShoppingBag className="w-3 h-3" /> Etsy
                </span>
              </div>

              <button data-testid="ai-shopper-btn" onClick={() => openShopper(lo)}
                className="w-full mt-3 flex items-center justify-center gap-2 text-sm bg-gradient-to-r from-primary/80 to-secondary/80 text-white rounded-lg py-2 hover:opacity-90">
                <Sparkles className="w-4 h-4" /> AI Shopper · Gift Ideas
              </button>
              <button data-testid="autonomous-draft-btn" onClick={() => runAutonomousDraft(lo)} disabled={autoDrafting === lo.id}
                className="w-full mt-2 flex items-center justify-center gap-2 text-sm bg-muted/40 border border-primary/30 text-primary rounded-lg py-2 hover:bg-primary/10 disabled:opacity-60">
                {autoDrafting === lo.id ? <><Loader2 className="w-4 h-4 animate-spin" /> Drafting card + gifts…</> : <><Eye className="w-4 h-4" /> Autonomous Draft</>}
              </button>
              {(lo.instagram_url || lo.linkedin_url || lo.pinterest_url) && (
                <button data-testid="social-pulse-btn" onClick={() => runPulse(lo)} disabled={pulsing === lo.id}
                  className={`w-full mt-2 flex items-center justify-center gap-2 text-sm rounded-lg py-2 disabled:opacity-60 ${lo.pulse_alert ? "wand-glow border border-yellow-500/40 bg-yellow-500/10 gold-text" : "surface text-muted-foreground hover:text-foreground"}`}>
                  {pulsing === lo.id ? <><Loader2 className="w-4 h-4 animate-spin" /> Scanning…</> : <><Radar className="w-4 h-4" /> {lo.pulse_alert ? "New life event detected!" : "Run Social Pulse"}</>}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="glass rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 animate-fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-2xl">{editId ? "Edit Loved One" : "New Loved One"}</h2>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-muted/50"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground">Name *</label>
                <input data-testid="lo-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full mt-1 bg-input border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="e.g. Mom" />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Relationship</label>
                <input data-testid="lo-relationship" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })}
                  className="w-full mt-1 bg-input border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="Mother, best friend, partner…" />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Personality summary</label>
                <textarea data-testid="lo-personality" value={form.personality} onChange={(e) => setForm({ ...form, personality: e.target.value })} rows={2}
                  className="w-full mt-1 bg-input border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none" placeholder="Warm, funny, loves the ocean…" />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Interests (helps the AI)</label>
                <input data-testid="lo-interests" value={form.interests} onChange={(e) => setForm({ ...form, interests: e.target.value })}
                  className="w-full mt-1 bg-input border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="Gardening, jazz, hiking…" />
              </div>

              <div>
                <label className="text-sm text-muted-foreground">Avatar color</label>
                <div className="flex gap-2 mt-2">
                  {COLORS.map((c) => (
                    <button key={c} onClick={() => setForm({ ...form, avatar_color: c })}
                      className={`w-7 h-7 rounded-full border-2 ${form.avatar_color === c ? "border-white scale-110" : "border-transparent"}`}
                      style={{ background: c }} />
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm text-muted-foreground flex items-center gap-1.5"><Cake className="w-3.5 h-3.5 gold-text" /> Date of birth <span className="text-[11px] text-muted-foreground/70">(powers the Celestial Card)</span></label>
                <input data-testid="lo-dob" type="date" value={form.date_of_birth || ""} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                  className="w-full mt-1 bg-input border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50" />
              </div>

              {/* Special dates */}
              <div>
                <label className="text-sm text-muted-foreground">Special dates</label>
                <div className="flex gap-2 mt-1">
                  <select data-testid="sd-label" value={sdLabel} onChange={(e) => setSdLabel(e.target.value)}
                    className="bg-input border border-border rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50">
                    {["Birthday", "Anniversary", "Graduation", "Wedding", "Milestone", "Holiday", "Just Because"].map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <input data-testid="sd-date" type="date" value={sdDate} onChange={(e) => setSdDate(e.target.value)}
                    className="flex-1 bg-input border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" />
                  <button data-testid="sd-add" onClick={addSpecialDate} className="px-3 rounded-lg bg-secondary/20 text-secondary border border-secondary/30 hover:bg-secondary/30"><Plus className="w-4 h-4" /></button>
                </div>
                <div className="space-y-1.5 mt-2">
                  {form.special_dates.map((sd, i) => (
                    <div key={`${sd.label}-${sd.date}-${i}`} className="flex items-center gap-2 text-sm bg-muted/30 rounded-lg px-3 py-2">
                      <Calendar className="w-3.5 h-3.5 gold-text" />
                      <span>{sd.label}</span>
                      <span className="text-muted-foreground">· {sd.date}</span>
                      <button onClick={() => removeSpecialDate(i)} className="ml-auto text-muted-foreground hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mailing address (for physical card delivery via Lob) */}
              <div>
                <label className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 gold-text" /> Mailing address (for physical cards)
                </label>
                <div className="grid gap-2 mt-1">
                  <input data-testid="addr-line1" value={form.address.address_line1}
                    onChange={(e) => setForm({ ...form, address: { ...form.address, address_line1: e.target.value } })}
                    className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="Street address" />
                  <input data-testid="addr-line2" value={form.address.address_line2}
                    onChange={(e) => setForm({ ...form, address: { ...form.address, address_line2: e.target.value } })}
                    className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="Apt / Suite (optional)" />
                  <div className="grid grid-cols-3 gap-2">
                    <input data-testid="addr-city" value={form.address.address_city}
                      onChange={(e) => setForm({ ...form, address: { ...form.address, address_city: e.target.value } })}
                      className="bg-input border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="City" />
                    <input data-testid="addr-state" value={form.address.address_state}
                      onChange={(e) => setForm({ ...form, address: { ...form.address, address_state: e.target.value } })}
                      className="bg-input border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="State" />
                    <input data-testid="addr-zip" value={form.address.address_zip}
                      onChange={(e) => setForm({ ...form, address: { ...form.address, address_zip: e.target.value } })}
                      className="bg-input border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="ZIP" />
                  </div>
                </div>
              </div>

              {/* Contact — for Living Capsule update notifications */}
              <div>
                <label className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 gold-text" /> Contact (for Living Capsule notifications)
                </label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <input data-testid="lo-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-input border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="Email (optional)" />
                  <input data-testid="lo-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="bg-input border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="Phone (optional)" />
                </div>
                <p className="text-[11px] text-muted-foreground/70 mt-1">We&apos;ll text/email them when you add a new Living Update to a sent card.</p>
              </div>

              {/* Pinterest board URL (AI Shopper) */}
              <div>
                <label className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <Link2 className="w-3.5 h-3.5 text-secondary" /> Pinterest board URL (powers AI Shopper)
                </label>
                <input data-testid="lo-pinterest-url" value={form.pinterest_url}
                  onChange={(e) => setForm({ ...form, pinterest_url: e.target.value, pinterest_linked: !!e.target.value })}
                  className="w-full mt-1 bg-input border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder="https://pinterest.com/username/board" />
              </div>

              {/* Public Social Profiles (Social Intelligence Pulse) */}
              <div>
                <label className="text-sm text-muted-foreground">Public social profiles (Social Pulse)</label>
                <input data-testid="lo-instagram-url" value={form.instagram_url}
                  onChange={(e) => setForm({ ...form, instagram_url: e.target.value })}
                  className="w-full mt-1 bg-input border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder="Instagram URL" />
                <input data-testid="lo-linkedin-url" value={form.linkedin_url}
                  onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })}
                  className="w-full mt-2 bg-input border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder="LinkedIn URL" />
                <p className="text-[11px] text-muted-foreground/70 mt-1">Weekly AI Pulse scans only for gifting milestones & aesthetic cues (simulated).</p>
              </div>

              {/* Platform bridge placeholders */}
              <div>
                <label className="text-sm text-muted-foreground">Platform Bridge (coming soon)</label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  <button onClick={() => toggle("social_connected")} data-testid="toggle-social"
                    className={`text-xs px-2 py-2 rounded-lg border flex items-center justify-center gap-1 ${form.social_connected ? "bg-secondary/15 text-secondary border-secondary/30" : "bg-muted/30 text-muted-foreground border-border"}`}>
                    <Instagram className="w-3.5 h-3.5" /> Social
                  </button>
                  <button onClick={() => toggle("pinterest_linked")} data-testid="toggle-pinterest"
                    className={`text-xs px-2 py-2 rounded-lg border flex items-center justify-center gap-1 ${form.pinterest_linked ? "bg-secondary/15 text-secondary border-secondary/30" : "bg-muted/30 text-muted-foreground border-border"}`}>
                    <Link2 className="w-3.5 h-3.5" /> Pinterest
                  </button>
                  <button onClick={() => toggle("etsy_linked")} data-testid="toggle-etsy"
                    className={`text-xs px-2 py-2 rounded-lg border flex items-center justify-center gap-1 ${form.etsy_linked ? "bg-secondary/15 text-secondary border-secondary/30" : "bg-muted/30 text-muted-foreground border-border"}`}>
                    <ShoppingBag className="w-3.5 h-3.5" /> Etsy
                  </button>
                </div>
              </div>

              <button data-testid="save-loved-one" onClick={save} disabled={saving}
                className="w-full bg-gradient-to-r from-primary to-secondary text-white font-medium rounded-lg py-3 hover:opacity-90 disabled:opacity-50">
                {saving ? "Saving…" : editId ? "Save Changes" : "Add Loved One"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* AI Shopper Modal */}
      {shopperOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setShopperOpen(false)}>
          <div className="glass rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 animate-fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                <h2 className="font-serif text-2xl">AI Shopper · {shopperLo?.name}</h2>
              </div>
              <button onClick={() => setShopperOpen(false)} className="p-1.5 rounded-lg hover:bg-muted/50"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Personalized gift mood boards from interests, personality {shopperLo?.pinterest_url ? "& linked Pinterest board" : ""}.
            </p>

            {/* Vision Agent panel */}
            <div className="glass rounded-xl p-4 mb-4 border border-secondary/20" data-testid="vision-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-secondary" />
                  <span className="font-medium text-sm">Vision Agent · Style Analysis</span>
                </div>
                {styleProfile && <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary/15 text-secondary border border-secondary/30">Analyzed</span>}
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Analyze {shopperLo?.pinterest_url ? "the linked Pinterest board and/or " : ""}uploaded reference images to learn their visual taste.
              </p>
              <div className="flex gap-2 mb-2">
                <label className="flex-1 cursor-pointer text-xs flex items-center justify-center gap-1.5 bg-muted/40 border border-border rounded-lg py-2 hover:bg-muted/60" data-testid="vision-upload-label">
                  <Upload className="w-3.5 h-3.5" /> {uploadedImages.length ? `${uploadedImages.length} image(s)` : "Upload reference images"}
                  <input type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} data-testid="vision-upload" />
                </label>
                <button data-testid="run-vision-btn" onClick={runVision} disabled={visionLoading}
                  className="text-xs flex items-center gap-1.5 bg-secondary/20 text-secondary border border-secondary/30 rounded-lg px-3 hover:bg-secondary/30 disabled:opacity-60">
                  {visionLoading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analyzing…</> : <><Eye className="w-3.5 h-3.5" /> Analyze Style</>}
                </button>
              </div>
              {visionError && <div data-testid="vision-error" className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-2 py-1.5">{visionError}</div>}
              {styleProfile && (
                <div className="mt-2 bg-muted/20 rounded-lg p-3" data-testid="style-profile">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className="text-sm font-medium gold-text">{styleProfile.aesthetic}</span>
                    <div className="flex gap-1">
                      {(styleProfile.palette || []).slice(0, 5).map((c, i) => <div key={i} className="w-4 h-4 rounded-full border border-white/20" style={{ background: c }} />)}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">{styleProfile.summary}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(styleProfile.materials || []).map((m, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary/10 text-secondary">{m}</span>)}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2 mb-5">
              <input data-testid="shopper-occasion" value={shopperOccasion} onChange={(e) => setShopperOccasion(e.target.value)}
                className="flex-1 bg-input border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="Occasion" />
              <button data-testid="run-shopper-btn" onClick={runShopper} disabled={shopperLoading}
                className="flex items-center gap-2 bg-gradient-to-r from-primary to-secondary text-white px-4 rounded-lg hover:opacity-90 disabled:opacity-60">
                {shopperLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Curating…</> : <><Sparkles className="w-4 h-4" /> Generate</>}
              </button>
            </div>

            {shopperError && <div data-testid="shopper-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 mb-4">{shopperError}</div>}

            {boards.length > 0 ? (
              <div className="grid md:grid-cols-3 gap-4" data-testid="mood-boards">
                {boards.map((b, i) => (
                  <div key={b.theme || i} className="glass rounded-xl p-4 border border-primary/20">
                    <div className="flex gap-1 mb-2">
                      {(b.palette || []).slice(0, 4).map((c, j) => <div key={j} className="w-5 h-5 rounded-full border border-white/20" style={{ background: c }} />)}
                    </div>
                    <h3 className="font-serif text-lg">{b.theme}</h3>
                    <p className="text-xs text-muted-foreground italic mb-3">{b.vibe}</p>
                    <div className="space-y-2">
                      {(b.gifts || []).map((g, k) => (
                        <div key={k} className="text-sm bg-muted/30 rounded-lg p-2.5">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium">{g.name}</span>
                            <span className="text-xs gold-text whitespace-nowrap">{g.price_range}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">{g.why}</p>
                          {(g.etsy_url || g.shopify_url) && (
                            <div className="flex gap-2">
                              {g.etsy_url && <a href={g.etsy_url} target="_blank" rel="noreferrer" className="text-[11px] flex items-center gap-1 text-secondary hover:underline">Etsy <ExternalLink className="w-3 h-3" /></a>}
                              {g.shopify_url && <a href={g.shopify_url} target="_blank" rel="noreferrer" className="text-[11px] flex items-center gap-1 text-primary hover:underline">Shop <ExternalLink className="w-3 h-3" /></a>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : !shopperLoading && (
              <div className="text-center py-10 text-muted-foreground border border-dashed border-border rounded-xl">
                <ShoppingBag className="w-8 h-8 mx-auto mb-2 text-primary/50" />
                Click Generate to curate 3 gift mood boards.
              </div>
            )}
          </div>
        </div>
      )}
    </AppShell>
  );
};

export default LovedOnes;
